import {
  MAX_CURRENCY,
  MAX_LENGTH,
  MAX_MATCHES,
  MAX_SCORE,
  cloneProgress,
  createDefaultProgress,
  validateProgress,
  type CosmeticId,
  type PerformancePreference,
  type Progress,
} from "./schema";

const V0_FIELDS = new Set([
  "schemaVersion",
  "bestScore",
  "matchesPlayed",
  "longestLength",
  "currency",
  "tutorialCompleted",
  "ownedCosmetics",
  "equippedCosmetic",
  "settings",
  "updatedAt",
]);
const VERSIONED_FIELDS = new Set([
  "schemaVersion",
  "revision",
  "updatedAt",
  "records",
  "settings",
  "tutorial",
  "currency",
  "ownedCosmetics",
  "equippedCosmetic",
]);
const V2_RECORD_FIELDS = new Set([
  "bestScore",
  "longestLength",
  "longestSurvivalMs",
  "matchesPlayed",
  "eliminations",
  "foodCollected",
  "totalScore",
  "totalSurvivalMs",
]);
const LEGACY_SETTINGS_FIELDS = new Set([
  "music",
  "sfx",
  "vibration",
  "reducedMotion",
  "reducedParticles",
  "performance",
]);
const V2_SETTINGS_FIELDS = new Set([...LEGACY_SETTINGS_FIELDS, "theme"]);
const TUTORIAL_FIELDS = new Set(["completed", "step"]);
const V2_COSMETIC_IDS = ["gloam", "coral", "lime", "constellation"] as const;
const V2_COSMETICS = new Set<string>(V2_COSMETIC_IDS);

interface LegacySettings {
  music: boolean;
  sfx: boolean;
  vibration: boolean;
  reducedMotion: boolean;
  reducedParticles: boolean;
  performance: PerformancePreference;
}

export type ProgressSchemaVersion = 0 | 1 | 2 | 3;

export interface MigrationResult {
  progress: Progress;
  migrated: boolean;
  sourceSchemaVersion: ProgressSchemaVersion;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const exactFields = (
  value: Record<string, unknown>,
  fields: Set<string>,
): boolean =>
  Object.keys(value).length === fields.size &&
  Object.keys(value).every((key) => fields.has(key));

const boundedInteger = (value: unknown, max: number): value is number =>
  typeof value === "number" &&
  Number.isSafeInteger(value) &&
  value >= 0 &&
  value <= max;

const isLegacySettings = (value: unknown): value is LegacySettings => {
  if (!isRecord(value) || !exactFields(value, LEGACY_SETTINGS_FIELDS))
    return false;
  return (
    typeof value.music === "boolean" &&
    typeof value.sfx === "boolean" &&
    typeof value.vibration === "boolean" &&
    typeof value.reducedMotion === "boolean" &&
    typeof value.reducedParticles === "boolean" &&
    (value.performance === "auto" ||
      value.performance === "quality" ||
      value.performance === "low")
  );
};

const upgradeV2 = (input: unknown): Progress | null => {
  if (
    !isRecord(input) ||
    !exactFields(input, VERSIONED_FIELDS) ||
    input.schemaVersion !== 2 ||
    !boundedInteger(input.revision, 1_000_000_000) ||
    !boundedInteger(input.updatedAt, Number.MAX_SAFE_INTEGER) ||
    !isRecord(input.records) ||
    !exactFields(input.records, V2_RECORD_FIELDS) ||
    !isRecord(input.settings) ||
    !exactFields(input.settings, V2_SETTINGS_FIELDS) ||
    !isRecord(input.tutorial) ||
    !exactFields(input.tutorial, TUTORIAL_FIELDS) ||
    !isRecord(input.ownedCosmetics)
  ) {
    return null;
  }
  const settings = input.settings;
  const owned = input.ownedCosmetics;
  if (
    !isLegacySettings(
      Object.fromEntries(
        [...LEGACY_SETTINGS_FIELDS].map((key) => [key, settings[key]]),
      ),
    ) ||
    (settings.theme !== "dark" && settings.theme !== "light") ||
    owned.gloam !== true ||
    Object.keys(owned).length > V2_COSMETIC_IDS.length ||
    Object.entries(owned).some(
      ([id, value]) => !V2_COSMETICS.has(id) || value !== true,
    ) ||
    typeof input.equippedCosmetic !== "string" ||
    !V2_COSMETICS.has(input.equippedCosmetic) ||
    owned[input.equippedCosmetic] !== true
  ) {
    return null;
  }

  const candidate = createDefaultProgress(input.updatedAt);
  candidate.revision = input.revision;
  candidate.records = {
    ...candidate.records,
    ...input.records,
  };
  candidate.settings = {
    ...(settings as unknown as Omit<
      Progress["settings"],
      "skipReadyCountdown"
    >),
    skipReadyCountdown: false,
  };
  candidate.tutorial = input.tutorial as unknown as Progress["tutorial"];
  candidate.currency = input.currency as number;
  candidate.ownedCosmetics = { ...owned } as Progress["ownedCosmetics"];
  candidate.equippedCosmetic = input.equippedCosmetic as CosmeticId;
  const validation = validateProgress(candidate);
  return validation.ok ? cloneProgress(validation.value) : null;
};

const v1ToV2 = (
  input: Record<string, unknown>,
): Record<string, unknown> | null => {
  if (
    !exactFields(input, VERSIONED_FIELDS) ||
    input.schemaVersion !== 1 ||
    !isLegacySettings(input.settings)
  ) {
    return null;
  }
  return {
    ...input,
    schemaVersion: 2,
    settings: { ...input.settings, theme: "dark" },
  };
};

const v0ToV2 = (
  input: Record<string, unknown>,
  now: number,
): Record<string, unknown> | null => {
  if (
    !exactFields(input, V0_FIELDS) ||
    input.schemaVersion !== 0 ||
    !boundedInteger(input.bestScore, MAX_SCORE) ||
    !boundedInteger(input.matchesPlayed, MAX_MATCHES) ||
    !boundedInteger(input.longestLength, MAX_LENGTH) ||
    !boundedInteger(input.currency, MAX_CURRENCY) ||
    typeof input.tutorialCompleted !== "boolean" ||
    !isLegacySettings(input.settings) ||
    !boundedInteger(input.updatedAt, Number.MAX_SAFE_INTEGER) ||
    !Array.isArray(input.ownedCosmetics) ||
    input.ownedCosmetics.length < 1 ||
    input.ownedCosmetics.length > V2_COSMETIC_IDS.length ||
    Object.keys(input.ownedCosmetics).length !== input.ownedCosmetics.length ||
    input.ownedCosmetics.some(
      (id) => typeof id !== "string" || !V2_COSMETICS.has(id),
    ) ||
    new Set(input.ownedCosmetics).size !== input.ownedCosmetics.length ||
    !input.ownedCosmetics.includes("gloam") ||
    typeof input.equippedCosmetic !== "string" ||
    !input.ownedCosmetics.includes(input.equippedCosmetic)
  ) {
    return null;
  }

  const progress = createDefaultProgress(now);
  return {
    ...progress,
    schemaVersion: 2,
    updatedAt: input.updatedAt,
    records: {
      bestScore: input.bestScore,
      longestLength: input.longestLength,
      longestSurvivalMs: 0,
      matchesPlayed: input.matchesPlayed,
      eliminations: 0,
      foodCollected: 0,
      totalScore: 0,
      totalSurvivalMs: 0,
    },
    settings: { ...input.settings, theme: "dark" },
    tutorial: input.tutorialCompleted
      ? { completed: true, step: 5 }
      : { completed: false, step: 0 },
    currency: input.currency,
    ownedCosmetics: Object.fromEntries(
      input.ownedCosmetics.map((id) => [id, true]),
    ),
    equippedCosmetic: input.equippedCosmetic,
  };
};

export const migrateProgress = (
  input: unknown,
  now = Date.now(),
): MigrationResult | null => {
  const current = validateProgress(input);
  if (current.ok) {
    return {
      progress: cloneProgress(current.value),
      migrated: false,
      sourceSchemaVersion: 3,
    };
  }
  if (!isRecord(input)) return null;

  if (input.schemaVersion === 2) {
    const progress = upgradeV2(input);
    return progress
      ? { progress, migrated: true, sourceSchemaVersion: 2 }
      : null;
  }
  if (input.schemaVersion === 1) {
    const v2 = v1ToV2(input);
    const progress = v2 ? upgradeV2(v2) : null;
    return progress
      ? { progress, migrated: true, sourceSchemaVersion: 1 }
      : null;
  }
  if (input.schemaVersion === 0) {
    const v2 = v0ToV2(input, now);
    const progress = v2 ? upgradeV2(v2) : null;
    return progress
      ? { progress, migrated: true, sourceSchemaVersion: 0 }
      : null;
  }
  return null;
};
